import React, { useState } from "react";
import classNames from "classnames";
import {
  TODAY,
  CITIES,
  CITY_CODE,
  RANGE_OPTIONS,
  TripItem,
  TripRange,
  ReservationItem,
  BoardStatus,
  PAY_STYLE,
  BOARD_STYLE,
  CLS_STYLE,
  tripById,
  tripsFor,
  takenSet,
  remainingSeats,
  seatLayout,
  hydrateReservation,
  generateTicketCode,
  money,
  num,
  fDate,
  fDateCap,
  fShort,
  fDash,
  pad,
} from "./reservation-types";

/* -------------------------------------------------------------------------- */
/*                                Shared bits                                 */
/* -------------------------------------------------------------------------- */

export const nowStamp = () => {
  const d = new Date();
  return `${fShort(TODAY)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
export const nowIso = () => {
  const d = new Date();
  return `${TODAY}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function Overlay({ z = "z-[85]", onClose, children, className }: { z?: string; onClose?: () => void; children: React.ReactNode; className?: string }) {
  return (
    <div className={classNames("fixed inset-0 flex items-center justify-center bg-[rgba(31,27,46,0.5)]", z, className ?? "p-5")} onMouseDown={onClose}>
      <div onMouseDown={(e) => e.stopPropagation()} className="contents">
        {children}
      </div>
    </div>
  );
}

function RadioDot({ on }: { on: boolean }) {
  return <span className="w-4 h-4 rounded-full border-2 border-[#6d28d9] shadow-[inset_0_0_0_2px_#fff] shrink-0" style={{ background: on ? "#6d28d9" : "#fff" }} />;
}

function RangePicker({ value, onChange }: { value: TripRange; onChange: (r: TripRange) => void }) {
  return (
    <>
      {RANGE_OPTIONS.map(([k, label]) => (
        <button key={k} type="button" onClick={() => onChange(k)} className="flex items-center gap-1.5 text-[13px] text-[#3f3a52] whitespace-nowrap">
          <RadioDot on={value === k} />
          {label}
        </button>
      ))}
    </>
  );
}

// One row of the trip list (wizard step 1 and bordereau).
function TripRow({
  t,
  list,
  excl,
  selected,
  btnLabel,
  showPrice,
  onPick,
  compact,
}: {
  t: TripItem;
  list: ReservationItem[];
  excl?: string[];
  selected?: boolean;
  btnLabel: string;
  showPrice?: boolean;
  onPick: (full: boolean) => void;
  compact?: boolean;
}) {
  const rem = remainingSeats(t, list, excl);
  const full = rem === 0;
  const c = CLS_STYLE[t.cls];
  return (
    <div
      className={classNames(
        "flex items-center gap-x-4 gap-y-2.5 flex-wrap",
        compact ? "py-2 px-1 border-b border-[#f1f0f5]" : "py-2.5 px-3.5 rounded-[11px] border-[1.5px]",
        !compact && (selected ? "border-[#6d28d9] bg-[#f5f1ff]" : "border-[#ece7f8] bg-white")
      )}
    >
      <div className="flex items-center gap-1.5 flex-[1_1_210px]">
        <span className="px-2 py-1 bg-[#fed7aa] rounded-full text-[11.5px] font-bold text-[#7c2d12] whitespace-nowrap">{t.from}</span>
        <span className="text-[#241f36]">→</span>
        <span className="px-2 py-1 bg-[#bbf7d0] rounded-full text-[11.5px] font-bold text-[#14532d] whitespace-nowrap">{t.to}</span>
      </div>
      <span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold whitespace-nowrap" style={{ backgroundColor: c.bg, color: c.fg }}>
        {t.cls}
      </span>
      <span className="flex-[1_1_150px] text-[12.5px] text-[#4a4361]">
        Départ le <strong className="text-[#241f36]">{fDateCap(t.date)}</strong>
      </span>
      <span className="text-[12.5px] text-[#241f36] font-semibold whitespace-nowrap">À {t.heure}</span>
      <span className="px-2 py-1 bg-[#f1f0f5] rounded-full font-mono text-[11.5px] text-[#3f3a52] whitespace-nowrap">{t.bus}</span>
      {showPrice && <span className="text-xs text-[#6b6480] whitespace-nowrap">{money(t.prix)}</span>}
      <span
        className="px-2.5 py-1 rounded-full text-[11.5px] font-bold whitespace-nowrap"
        style={{ background: full ? "#fee2e2" : rem < 10 ? "#fef3c7" : "#f1f0f5", color: full ? "#b91c1c" : rem < 10 ? "#b45309" : "#3f3a52" }}
      >
        {rem} restants
      </span>
      <button
        type="button"
        onClick={() => onPick(full)}
        className={classNames(
          "h-[34px] px-3 rounded-lg text-[12.5px] font-semibold border whitespace-nowrap",
          selected ? "bg-[#6d28d9] border-[#6d28d9] text-white" : "bg-white border-[#d4d4d8] text-[#241f36] hover:bg-gray-50"
        )}
      >
        {selected ? "Sélectionné ✓" : btnLabel}
      </button>
    </div>
  );
}

// Bus seat map. `onPick` omitted = read-only.
export function SeatMap({
  t,
  taken,
  selected = [],
  highlight,
  onPick,
  small,
}: {
  t: TripItem;
  taken: Set<number>;
  selected?: number[];
  highlight?: number | null;
  onPick?: (n: number) => void;
  small?: boolean;
}) {
  return (
    <div className={classNames("grid justify-center", small ? "gap-[5px]" : "gap-1.5")}>
      {seatLayout(t).map((row, i) => (
        <div key={i} className={classNames("flex", small ? "gap-[5px]" : "gap-1.5")}>
          {row.map((n, j) => {
            if (n === null) return <span key={j} className={small ? "w-[30px]" : "w-9"} />;
            const sel = selected.includes(n) || n === highlight;
            const tk = taken.has(n) && !sel;
            return (
              <button
                key={j}
                type="button"
                disabled={tk || !onPick}
                onClick={() => onPick?.(n)}
                className={classNames(
                  "flex items-center justify-center font-bold rounded-[7px_7px_4px_4px] border-[1.5px]",
                  small ? "w-[30px] h-[26px] text-[10.5px]" : "w-9 h-8 text-[11px]",
                  tk || !onPick ? "cursor-default" : "cursor-pointer"
                )}
                style={{
                  background: sel ? "#6d28d9" : tk ? "#e7e5ee" : "#fff",
                  color: sel ? "#fff" : tk ? "#a39fb3" : "#3f3a52",
                  borderColor: sel ? "#6d28d9" : tk ? "#e7e5ee" : "#c4b5fd",
                }}
              >
                {n}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Wizard                                   */
/* -------------------------------------------------------------------------- */

export type WizMode = "resa" | "ticket" | "confirm";

export interface WizState {
  mode: WizMode;
  step: number;
  target: string | null;
  trip: string | null; // trip id, "OPEN" for an open trip
  range: TripRange;
  q: string;
  cls: string;
  name: string;
  cc: string;
  phone: string;
  idType: string;
  idNum: string;
  adv: boolean;
  baby: boolean;
  minor: boolean;
  proches: { name: string; phone: string }[];
  urgence: string;
  seats: number[];
  bag: boolean;
  oFrom: string;
  oTo: string;
  oDate: string;
  oHeure: string;
  oCls: "CLASSIQUE" | "VIP";
  oPrix: string;
  pay: "link" | "ref" | "cash";
  refOM: string;
  refMomo: string;
  wish: string;
  doneIds: string[];
}

export const newWiz = (mode: WizMode): WizState => ({
  mode,
  step: 1,
  target: null,
  trip: null,
  range: "today",
  q: "",
  cls: "TOUS",
  name: "",
  cc: "+237",
  phone: "",
  idType: "CNI",
  idNum: "",
  adv: false,
  baby: false,
  minor: false,
  proches: [],
  urgence: "",
  seats: [],
  bag: false,
  oFrom: "DOUALA",
  oTo: "YAOUNDÉ",
  oDate: TODAY,
  oHeure: "",
  oCls: "CLASSIQUE",
  oPrix: "",
  pay: mode === "ticket" ? "cash" : "link",
  refOM: "",
  refMomo: "",
  wish: "",
  doneIds: [],
});

// Wizard pre-filled from an existing reservation (confirm / edit / change trip).
export const wizFor = (r: ReservationItem, startStep?: number): WizState => {
  const w = newWiz("confirm");
  Object.assign(w, {
    target: r.id,
    name: r.name,
    phone: r.phone.replace(/^\+?237/, ""),
    idNum: r.cni,
    trip: r.trip || null,
    seats: r.seat ? [r.seat] : [],
    pay: r.pay === "Payé" ? "ref" : "link",
    refOM: r.refOM,
    refMomo: r.refMomo,
    bag: r.bag,
    range: "7j",
    q: r.trip ? "" : r.to,
    wish: r.trip ? "" : `${r.from} → ${r.to}${r.date ? ` · ${fShort(r.date)}${r.heure ? ` à ${r.heure}` : ""}` : ""}`,
    oFrom: r.from || "DOUALA",
    oTo: r.to || "YAOUNDÉ",
    oDate: r.date || TODAY,
    oHeure: r.heure,
  });
  w.step = startStep || (r.trip ? 2 : 1);
  return w;
};

// Applies a completed wizard to the list: updates the target reservation and/or creates new ones (one per passenger).
export function applyWizard(list: ReservationItem[], seq: number, w: WizState, agent: string) {
  const t = w.trip && w.trip !== "OPEN" ? tripById(w.trip) : undefined;
  const st = nowStamp();
  const iso = nowIso();
  const paidNow = w.pay !== "link";
  const main = w.phone.replace(/\s/g, "");
  const pax = [{ name: w.name.trim().toUpperCase(), phone: w.cc + main, cni: w.idNum }].concat(
    w.proches.filter((p) => p.name.trim()).map((p) => ({ name: p.name.trim().toUpperCase(), phone: w.cc + (p.phone.replace(/\s/g, "") || main), cni: "" }))
  );
  const prix = t ? t.prix : num(w.oPrix);
  const where: Partial<ReservationItem> = t ? {} : { from: w.oFrom, to: w.oTo, date: w.oDate, heure: w.oHeure, cls: w.oCls };
  let next = list.slice();
  const ids: string[] = [];
  pax.forEach((p, i) => {
    const montant = prix + (i === 0 && w.bag ? 1000 : 0) + (i === 0 && !paidNow ? 250 : 0);
    const canal = w.pay === "cash" ? "Espèces" : w.refOM ? "Orange Money" : w.refMomo ? "MTN MoMo" : "Mobile Money";
    const patch: Partial<ReservationItem> = {
      name: p.name,
      phone: p.phone,
      cni: p.cni,
      trip: t ? t.id : "",
      seat: t ? w.seats[i] || null : null,
      montant,
      paid: paidNow ? montant : 0,
      pay: paidNow ? "Payé" : "Non payé",
      confirmed: paidNow,
      confirmedBy: paidNow ? agent : "",
      confirmedAt: paidNow ? iso : "",
      ticket: paidNow ? generateTicketCode() : "",
      refOM: w.pay === "ref" ? w.refOM : "",
      refMomo: w.pay === "ref" ? w.refMomo : "",
      canal: paidNow ? canal : "—",
      linkSent: !paidNow,
      bag: i === 0 && w.bag,
      board: "En attente",
      note: w.urgence ? `Contact d'urgence : ${w.urgence}` : "",
      ...where,
    };
    const ev: [string, string] = [
      st,
      paidNow ? `Billet émis par ${agent}${patch.seat ? ` · siège ${patch.seat}` : ""} · ${canal}` : "Lien de paiement Mobile Money envoyé par SMS / WhatsApp",
    ];
    if (i === 0 && w.target) {
      next = next.map((r) => (r.id === w.target ? hydrateReservation({ ...r, ...patch, pre: r.pre, audit: [...r.audit, ev] }) : r));
      ids.push(w.target);
    } else {
      seq++;
      const id = `n${seq}`;
      const from = t ? t.from : w.oFrom;
      next.unshift(
        hydrateReservation({
          id,
          createdBy: agent,
          createdAt: iso,
          source: w.mode === "ticket" ? "GUICHET" : "AGENT",
          agence: "Agence AKWA",
          pre: paidNow ? "" : `2809-${CITY_CODE[from] || "CMR"}-${28010 + seq}`,
          ticket: "",
          from: "",
          to: "",
          date: "",
          heure: "",
          cls: "CLASSIQUE",
          name: "",
          phone: "",
          cni: "",
          trip: "",
          seat: null,
          pay: "Non payé",
          refOM: "",
          refMomo: "",
          canal: "—",
          montant: 0,
          paid: 0,
          confirmed: false,
          confirmedBy: "",
          confirmedAt: "",
          board: "En attente",
          bag: false,
          linkSent: false,
          note: "",
          ...patch,
          audit: [[st, `Réservation créée par ${agent}`], ev],
        } as ReservationItem)
      );
      ids.push(id);
    }
  });
  return { list: next, seq, ids };
}

const PAY_MODES = [
  { k: "link" as const, title: "Lien de paiement Mobile Money", sub: "Orange Money ou MTN MoMo. Le lien part par SMS et WhatsApp ; le billet est émis dès réception. Frais de service 250 FCFA." },
  { k: "ref" as const, title: "Paiement déjà reçu", sub: "Saisir la référence Orange Money ou MoMo transmise par le client. Le billet est émis immédiatement." },
  { k: "cash" as const, title: "Espèces au guichet", sub: "Encaissement en agence. Le billet est émis et imprimé tout de suite." },
];

export function ReservationWizardModal({
  wiz,
  setWiz,
  list,
  onClose,
  onCommit,
  onShowTicket,
  onFlash,
}: {
  wiz: WizState;
  setWiz: (w: WizState) => void;
  list: ReservationItem[];
  onClose: () => void;
  onCommit: (w: WizState) => string[];
  onShowTicket: (id: string) => void;
  onFlash: (msg: string) => void;
}) {
  const w = wiz;
  const setW = (p: Partial<WizState>) => setWiz({ ...w, ...p });
  const t = w.trip && w.trip !== "OPEN" ? tripById(w.trip) : undefined;
  const open = w.trip === "OPEN";
  const excl = w.target ? [w.target] : [];
  const need = 1 + w.proches.length;
  const step = w.step;
  const trips = tripsFor(w.range, w.q, w.cls);
  const prix = t ? t.prix : open ? num(w.oPrix) : 0;
  const bagFee = w.bag ? 1000 : 0;
  const service = w.pay === "link" ? 250 : 0;
  const total = prix * need + bagFee + service;
  const phoneOk = w.phone.replace(/\D/g, "").length >= 8;
  const nextDisabled =
    (step === 1 && !w.trip) ||
    (step === 2 && (!w.name.trim() || !phoneOk)) ||
    (step === 3 && (open ? prix <= 0 : w.seats.length < need)) ||
    (step === 4 && w.pay === "ref" && !w.refOM.trim() && !w.refMomo.trim());
  const target = list.find((r) => r.id === w.target);
  const title = w.mode === "ticket" ? "Vente de billet au guichet" : w.mode === "confirm" ? `Confirmer la réservation${target?.pre ? ` · ${target.pre}` : ""}` : "Nouvelle réservation";
  const color = w.mode === "ticket" ? "#3b82f6" : w.mode === "confirm" ? "#22c55e" : "#f97316";
  const seatsTxt = w.seats.length ? w.seats.slice().sort((a, b) => a - b).join(", ") : "aucun";
  const cs = t
    ? { route: `${t.from} → ${t.to}`, when: `${fDateCap(t.date)} à ${t.heure}`, bus: `${t.bus} · ${t.cls}`, prix: money(t.prix) }
    : open
    ? { route: `${w.oFrom} → ${w.oTo}`, when: `Souhaité ${fShort(w.oDate)}${w.oHeure ? ` à ${w.oHeure}` : ""}`, bus: `Voyage indéterminé · ${w.oCls}`, prix: prix ? money(prix) : "à saisir" }
    : { route: "—", when: "—", bus: "—", prix: "—" };
  const done = list.filter((r) => w.doneIds.includes(r.id));
  const doneFirst = done[0];

  const pickSeat = (n: number) => {
    const cur = w.seats.slice();
    const i = cur.indexOf(n);
    if (i > -1) cur.splice(i, 1);
    else if (cur.length < need) cur.push(n);
    else {
      cur.pop();
      cur.push(n);
    }
    setW({ seats: cur });
  };

  const next = () => {
    if (nextDisabled) return;
    if (step === 4) setW({ step: 5, doneIds: onCommit(w) });
    else if (step === 5) onClose();
    else setW({ step: step + 1 });
  };
  const back = () => {
    if (step === 1) onClose();
    else if (step === 5) setWiz(newWiz(w.mode === "confirm" ? "resa" : w.mode));
    else setW({ step: step - 1 });
  };

  const inputCls = "w-full h-10 px-3 border border-[#e2e8f0] rounded-[9px] text-[13.5px] bg-white";
  const labelCls = "block text-[12.5px] font-semibold text-[#3f3a52] mb-1";

  return (
    <Overlay className="p-0 md:p-6">
      <div className="w-full max-w-[980px] h-full md:h-[min(90vh,860px)] flex flex-col bg-[#f8f7fc] md:rounded-[18px] overflow-hidden shadow-[0_30px_70px_rgba(31,27,46,0.35)] text-[#1f1b2e]">
        {/* Header + steps */}
        <div className="px-5 pt-4 pb-3.5 bg-white border-b border-[#ece7f8]">
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: color }} />
            <div className="flex-1 text-[17px] font-bold text-[#241f36]">{title}</div>
            <button type="button" onClick={onClose} className="w-[34px] h-[34px] border border-[#ece7f8] bg-white rounded-[9px] text-base text-[#6b6480]">
              ×
            </button>
          </div>
          <div className="flex items-center gap-2 mt-3.5">
            {["Voyage", "Infos client", "Siège", "Paiement"].map((label, i) => {
              const n = i + 1;
              const dn = step > n;
              const cur = step === n;
              return (
                <div key={label} className="flex-1 flex items-center gap-2 min-w-0">
                  <span className="shrink-0 w-[26px] h-[26px] rounded-full flex items-center justify-center text-xs font-bold" style={{ background: dn ? "#22c55e" : cur ? "#6d28d9" : "#ede9fe", color: dn || cur ? "#fff" : "#7c729b" }}>
                    {dn ? "✓" : n}
                  </span>
                  <span className={classNames("text-[12.5px] font-semibold truncate", !cur && "hidden md:inline")} style={{ color: cur ? "#241f36" : "#8b81a8" }}>
                    {label}
                  </span>
                  <span className="flex-1 h-0.5 min-w-2 rounded" style={{ background: i === 3 ? "transparent" : dn ? "#22c55e" : "#e6e0f5" }} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-[18px] text-[13px]">
          {step === 1 && (
            <>
              <div className="text-lg font-bold text-[#241f36] text-center">Liste des voyages</div>
              {w.wish && (
                <div className="mt-2.5 px-3 py-2 bg-[#fff7ed] border border-[#fed7aa] rounded-[9px] text-[12.5px] text-[#9a3412]">
                  Souhait du client : <strong>{w.wish}</strong>
                </div>
              )}
              <div className="flex items-center gap-3.5 flex-wrap mt-3.5">
                <RangePicker value={w.range} onChange={(range) => setW({ range })} />
                <input value={w.q} onChange={(e) => setW({ q: e.target.value })} placeholder="Recherche par ville / bus" className="flex-1 min-w-[180px] h-[38px] px-3 rounded-[9px] bg-[#e9e6f2] text-[13px] focus:outline-none focus:ring-2 focus:ring-purple-300" />
                <button
                  type="button"
                  onClick={() => {
                    setW({ q: "", cls: "TOUS" });
                    onFlash("Liste des voyages actualisée");
                  }}
                  className="h-[38px] px-3 bg-[#fee2e2] rounded-[9px] text-[12.5px] font-semibold text-[#dc2626] whitespace-nowrap"
                >
                  Actualiser ↻
                </button>
                <select value={w.cls} onChange={(e) => setW({ cls: e.target.value })} className="h-[38px] px-2.5 border border-[#e2e8f0] rounded-[9px] bg-white text-[13px]">
                  {["TOUS", "CLASSIQUE", "VIP"].map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="grid gap-2 mt-3.5">
                {trips.map((x) => (
                  <TripRow
                    key={x.id}
                    t={x}
                    list={list}
                    excl={excl}
                    selected={w.trip === x.id}
                    btnLabel="Sélectionner"
                    showPrice
                    onPick={(full) => {
                      if (full) return onFlash("Voyage complet — choisissez un autre départ");
                      setW({ trip: x.id, seats: x.id === w.trip ? w.seats : [], step: 2 });
                    }}
                  />
                ))}
                {trips.length === 0 && <div className="p-6 border border-dashed border-[#ddd6fe] rounded-xl text-center text-[#8b81a8]">Aucun voyage pour ces critères.</div>}
              </div>
              <div className="flex justify-center mt-3.5">
                <button type="button" onClick={() => setW({ trip: "OPEN", seats: [], step: 2 })} className="h-[38px] px-4 border border-[#d4d4d8] bg-white rounded-[9px] font-semibold text-[#241f36] hover:bg-gray-50">
                  Réservation sur voyage indéterminé
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <TripBanner cs={cs} />
              <div className="p-4 bg-white border border-[#ece7f8] rounded-xl mt-3.5">
                <div className="text-sm font-bold text-[#241f36]">Infos client</div>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3 mt-3">
                  <label className="block">
                    <span className={labelCls}>Nom complet du client</span>
                    <input value={w.name} onChange={(e) => setW({ name: e.target.value })} placeholder="Ex. MOFFO ARIELLE" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Téléphone</span>
                    <div className="flex gap-1.5">
                      <select value={w.cc} onChange={(e) => setW({ cc: e.target.value })} className="w-[84px] h-10 px-1.5 border border-[#e2e8f0] rounded-[9px] bg-white text-[13px]">
                        {["+237", "+236", "+241", "+240", "+33"].map((o) => (
                          <option key={o}>{o}</option>
                        ))}
                      </select>
                      <input value={w.phone} onChange={(e) => setW({ phone: e.target.value })} inputMode="tel" placeholder="6 74 84 57 49" className={classNames(inputCls, "flex-1 min-w-0")} />
                    </div>
                  </label>
                  <label className="block">
                    <span className={labelCls}>Type de pièce d'identité</span>
                    <select value={w.idType} onChange={(e) => setW({ idType: e.target.value })} className={inputCls}>
                      {["CNI", "Passeport", "Récépissé", "Permis de conduire", "Carte scolaire"].map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className={labelCls}>Numéro de pièce d'identité</span>
                    <input value={w.idNum} onChange={(e) => setW({ idNum: e.target.value })} placeholder="Numéro de pièce" className={inputCls} />
                  </label>
                </div>
                {!w.phone || phoneOk ? null : <div className="mt-2 text-xs text-rose-700">Le téléphone doit comporter au moins 8 chiffres.</div>}
                <button type="button" onClick={() => setW({ adv: !w.adv })} className="mt-3.5 text-[12.5px] font-semibold text-[#6d28d9]">
                  {w.adv ? "Formulaire avancé ▴" : "Formulaire avancé ▾"}
                </button>
                {w.adv && (
                  <div className="mt-3 pt-3 border-t border-[#f1ecfe]">
                    <div className="flex flex-wrap gap-x-6 gap-y-2.5">
                      {(
                        [
                          ["baby", "Le passager a-t-il un bébé ?"],
                          ["minor", "Le passager est-il mineur ?"],
                          ["bag", "Bagage supplémentaire en soute (+1 000 FCFA)"],
                        ] as const
                      ).map(([k, label]) => (
                        <label key={k} className="flex items-center gap-2 text-[13px] text-[#3f3a52] cursor-pointer">
                          <input type="checkbox" checked={w[k]} onChange={() => setW({ [k]: !w[k] } as Partial<WizState>)} className="w-4 h-4 accent-[#6d28d9]" />
                          {label}
                        </label>
                      ))}
                    </div>
                    <div className="grid gap-2 mt-3">
                      {w.proches.map((p, i) => (
                        <div key={i} className="flex gap-2 items-center flex-wrap">
                          <span className="text-xs font-bold text-[#15803d] w-[70px]">Proche {i + 1}</span>
                          <input
                            value={p.name}
                            onChange={(e) => setW({ proches: w.proches.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })}
                            placeholder="Nom complet"
                            className="flex-1 min-w-[160px] h-[38px] px-3 border border-[#e2e8f0] rounded-[9px] text-[13px]"
                          />
                          <input
                            value={p.phone}
                            onChange={(e) => setW({ proches: w.proches.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)) })}
                            placeholder="Téléphone (facultatif)"
                            className="flex-1 min-w-[140px] h-[38px] px-3 border border-[#e2e8f0] rounded-[9px] text-[13px]"
                          />
                          <button
                            type="button"
                            onClick={() => setW({ proches: w.proches.filter((_, j) => j !== i), seats: w.seats.slice(0, need - 1) })}
                            className="w-8 h-8 border border-[#fecaca] bg-white rounded-lg text-[#dc2626]"
                            title="Retirer"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() => setW({ proches: [...w.proches, { name: "", phone: "" }] })} className="mt-2.5 h-8 px-3 bg-[#dcfce7] rounded-lg text-[12.5px] font-semibold text-[#15803d]">
                      + Ajouter des proches (même voyage)
                    </button>
                    <textarea
                      value={w.urgence}
                      onChange={(e) => setW({ urgence: e.target.value })}
                      placeholder="Personne à contacter en cas d'urgence"
                      className="block w-full min-h-[60px] mt-3 px-3 py-2.5 border border-[#e2e8f0] rounded-[9px] text-[13px] resize-y"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {step === 3 &&
            (open ? (
              <div className="p-4 bg-white border border-[#ece7f8] rounded-xl">
                <div className="text-sm font-bold text-[#241f36]">Voyage indéterminé</div>
                <div className="text-[12.5px] text-[#6b6480] mt-1">Le siège et le bus seront attribués à l'embarquement. Indiquez le souhait du client.</div>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3 mt-3.5">
                  <label className="block">
                    <span className="block text-xs font-semibold text-[#3f3a52] mb-1">Départ</span>
                    <select value={w.oFrom} onChange={(e) => setW({ oFrom: e.target.value })} className={inputCls}>
                      {CITIES.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-xs font-semibold text-[#3f3a52] mb-1">Arrivée</span>
                    <select value={w.oTo} onChange={(e) => setW({ oTo: e.target.value })} className={inputCls}>
                      {CITIES.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-xs font-semibold text-[#3f3a52] mb-1">Date départ souhaitée</span>
                    <input type="date" value={w.oDate} onChange={(e) => setW({ oDate: e.target.value })} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-semibold text-[#3f3a52] mb-1">Heure départ souhaitée</span>
                    <input type="time" value={w.oHeure} onChange={(e) => setW({ oHeure: e.target.value })} className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-semibold text-[#3f3a52] mb-1">Type de bus</span>
                    <select value={w.oCls} onChange={(e) => setW({ oCls: e.target.value as WizState["oCls"] })} className={inputCls}>
                      <option value="CLASSIQUE">CLASSIQUE</option>
                      <option value="VIP">VIP</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-xs font-semibold text-[#3f3a52] mb-1">Prix (FCFA)</span>
                    <input value={w.oPrix} onChange={(e) => setW({ oPrix: e.target.value })} inputMode="numeric" placeholder="0" className={inputCls} />
                  </label>
                </div>
              </div>
            ) : (
              t && (
                <div className="flex flex-wrap gap-4 items-start">
                  <div className="flex-[1_1_300px] p-4 bg-white border border-[#ece7f8] rounded-xl">
                    <div className="flex justify-between flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-[#8b81a8] mb-3">
                      <span>Avant du bus · chauffeur</span>
                      <span>{cs.bus}</span>
                    </div>
                    <SeatMap t={t} taken={takenSet(t, list, excl)} selected={w.seats} onPick={pickSeat} />
                  </div>
                  <div className="flex-[1_1_260px] p-4 bg-white border border-[#ece7f8] rounded-xl">
                    <div className="text-sm font-bold text-[#241f36]">Choix du siège</div>
                    <div className="text-[12.5px] text-[#6b6480] mt-1">
                      {need > 1 ? `Choisissez ${need} sièges (vous + ${need - 1} proche${need > 2 ? "s" : ""}).` : "Cliquez sur un siège libre."}
                    </div>
                    <div className="text-[22px] font-bold text-[#6d28d9] mt-2.5">{w.seats.length ? `Siège${w.seats.length > 1 ? "s" : ""} ${seatsTxt}` : "Aucun siège"}</div>
                    <div className="grid gap-1.5 mt-3.5 text-xs text-[#4a4361]">
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-3.5 rounded border-[1.5px] border-[#c4b5fd] bg-white" />
                        Libre
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-3.5 rounded bg-[#6d28d9]" />
                        Sélectionné
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-3.5 rounded bg-[#e7e5ee]" />
                        Occupé
                      </span>
                    </div>
                  </div>
                </div>
              )
            ))}

          {step === 4 && (
            <>
              <div className="p-5 bg-[#2e1065] rounded-[14px] text-white">
                <div className="text-lg font-bold">Récapitulatif et paiement</div>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3 mt-3.5">
                  <RecapCell label="Passager" value={w.name.trim().toUpperCase() || "—"} sub={`${w.cc} ${w.phone}`} />
                  <RecapCell label="Trajet" value={cs.route} sub={cs.when} />
                  <RecapCell label="Bus / siège" value={cs.bus} sub={open ? "Siège à l'embarquement" : `Siège${w.seats.length > 1 ? "s" : ""} ${seatsTxt}`} />
                </div>
                <div className="grid gap-2 mt-4 pt-3.5 border-t border-[#5b21b6] text-[13.5px]">
                  <FeeLine label={`Billet${need > 1 ? "s" : ""} (${need} × ${money(prix)})`} amount={prix * need} />
                  {w.bag && <FeeLine label="Bagage supplémentaire" amount={bagFee} />}
                  {service > 0 && <FeeLine label="Frais de service Mobile Money" amount={service} />}
                </div>
                <div className="flex justify-between items-end mt-3.5 pt-3 border-t border-[#5b21b6]">
                  <span className="text-sm font-bold">Montant total</span>
                  <span className="text-[26px] font-bold">{money(total)}</span>
                </div>
              </div>
              <div className="p-4 bg-white border border-[#ece7f8] rounded-[14px] mt-3.5">
                <div className="text-[15px] font-bold text-[#241f36]">Mode de paiement</div>
                <div className="grid gap-2 mt-3">
                  {PAY_MODES.map((p) => {
                    const on = w.pay === p.k;
                    return (
                      <button
                        key={p.k}
                        type="button"
                        onClick={() => setW({ pay: p.k })}
                        className="flex items-start gap-3 px-3.5 py-3 border-[1.5px] rounded-[11px] text-left"
                        style={{ borderColor: on ? "#6d28d9" : "#e2e8f0", background: on ? "#f5f1ff" : "#fff" }}
                      >
                        <span className="shrink-0 w-[18px] h-[18px] mt-px rounded-full border-2 shadow-[inset_0_0_0_2px_#fff]" style={{ borderColor: on ? "#6d28d9" : "#cbd5e1", background: on ? "#6d28d9" : "#fff" }} />
                        <span>
                          <span className="block text-[13.5px] font-bold text-[#241f36]">{p.title}</span>
                          <span className="block text-xs text-[#6b6480] mt-0.5 leading-snug">{p.sub}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                {w.pay === "ref" && (
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-2.5 mt-3">
                    <input value={w.refOM} onChange={(e) => setW({ refOM: e.target.value })} placeholder="Réf. Orange Money (MP…)" className="h-10 px-3 border border-[#fed7aa] rounded-[9px] font-mono text-[13px]" />
                    <input value={w.refMomo} onChange={(e) => setW({ refMomo: e.target.value })} placeholder="Réf. MTN MoMo (MM…)" className="h-10 px-3 border border-[#fde68a] rounded-[9px] font-mono text-[13px]" />
                  </div>
                )}
              </div>
            </>
          )}

          {step === 5 && (
            <div className="max-w-[460px] mx-auto mt-2.5 px-5 py-6 bg-white border border-[#ece7f8] rounded-2xl text-center">
              <div className="w-[58px] h-[58px] mx-auto rounded-full border-4 border-[#22c55e] flex items-center justify-center text-[26px] font-bold text-[#16a34a]">✓</div>
              <div className="text-[17px] font-bold text-[#16a34a] mt-3">{done.some((r) => r.ticket) ? "Billet émis" : "Pré-réservation enregistrée"}</div>
              <div className="text-[12.5px] text-[#6b6480] mt-1.5 leading-normal">
                {done.some((r) => r.ticket)
                  ? "Le passager doit se présenter en agence au plus tard 30 minutes avant le départ."
                  : "Le lien de paiement est parti par SMS et WhatsApp. Le billet sera émis automatiquement après paiement."}
              </div>
              <div className="grid gap-2 mt-4 text-left">
                {done.map((r) => (
                  <div key={r.id} className="flex items-center gap-2.5 px-3 py-2.5 bg-[#f8f7fc] rounded-[10px]">
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13px] font-bold text-[#241f36]">{r.name}</span>
                      <span className="block text-[11.5px] text-[#6b6480]">{r.seat ? `Siège ${r.seat} · ${r.from} → ${r.to}` : "Siège à l'embarquement"}</span>
                    </span>
                    <span className="font-mono text-[13px] text-[#6d28d9]">{r.ticket || r.pre}</span>
                  </div>
                ))}
              </div>
              <div className="grid gap-2 mt-4">
                {doneFirst?.ticket && (
                  <button type="button" onClick={() => onShowTicket(doneFirst.id)} className="h-[42px] bg-[#6d28d9] rounded-[10px] text-[13.5px] font-bold text-white">
                    Voir et imprimer le ticket
                  </button>
                )}
                <button type="button" onClick={() => onFlash(`Envoyé par WhatsApp à ${doneFirst ? doneFirst.phone : ""}`)} className="h-[42px] bg-[#059669] rounded-[10px] text-[13.5px] font-bold text-white">
                  Envoyer par WhatsApp
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-t border-[#ece7f8]">
          <div className="flex-1 min-w-[150px]">
            <div className="text-[11.5px] text-[#6b6480]">{step === 1 ? "Voyage" : "Total à payer"}</div>
            <div className="text-[19px] font-bold text-[#4c1d95]">{step === 1 ? (w.trip ? cs.route : "Aucun voyage sélectionné") : money(total)}</div>
            <div className="text-[11px] text-[#8b81a8]">
              {step === 1 ? (w.trip ? cs.when : "") : `${need} passager${need > 1 ? "s" : ""} · ${open ? "voyage indéterminé" : `siège ${seatsTxt}`}`}
            </div>
          </div>
          <button type="button" onClick={back} className="h-[42px] px-4 border border-[#e2e8f0] bg-white rounded-[10px] text-[13px] font-semibold text-[#475569]">
            {step === 1 ? "Fermer" : step === 5 ? "Nouvelle réservation" : "Précédent"}
          </button>
          <button
            type="button"
            onClick={next}
            disabled={nextDisabled}
            className="h-[42px] px-[18px] rounded-[10px] text-[13px] font-bold text-white disabled:cursor-not-allowed"
            style={{ background: nextDisabled ? "#c4b5fd" : step === 4 ? "#16a34a" : "#6d28d9" }}
          >
            {step === 4 ? (w.pay === "link" ? "Envoyer le lien de paiement" : "Confirmer et émettre le billet") : step === 5 ? "Terminer" : "Suivant"}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

function TripBanner({ cs }: { cs: { route: string; when: string; bus: string; prix: string } }) {
  return (
    <div className="flex flex-wrap gap-x-[26px] gap-y-3 px-4 py-3.5 bg-[#4c1d95] rounded-xl text-white">
      {(
        [
          ["Trajet", cs.route],
          ["Départ", cs.when],
          ["Bus", cs.bus],
          ["Prix unitaire", cs.prix],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <div className="text-[10.5px] tracking-[0.07em] uppercase text-[#ddd6fe]">{label}</div>
          <div className="text-[13px] font-bold mt-0.5">{value}</div>
        </div>
      ))}
    </div>
  );
}

function RecapCell({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div>
      <div className="text-[10.5px] tracking-[0.07em] uppercase text-[#c4b5fd]">{label}</div>
      <div className="text-[13.5px] font-bold mt-0.5">{value}</div>
      <div className="text-xs text-[#ddd6fe]">{sub}</div>
    </div>
  );
}

function FeeLine({ label, amount }: { label: string; amount: number }) {
  return (
    <div className="flex justify-between gap-3">
      <span>{label}</span>
      <span className="font-bold whitespace-nowrap">{money(amount)}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                 Bordereau                                  */
/* -------------------------------------------------------------------------- */

export function BordereauModal({
  list,
  onClose,
  onFlash,
  onToggleBoard,
  onOpenTripResa,
}: {
  list: ReservationItem[];
  onClose: () => void;
  onFlash: (msg: string) => void;
  onToggleBoard: (id: string, status: BoardStatus) => void;
  onOpenTripResa: () => void;
}) {
  const [range, setRange] = useState<TripRange>("today");
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("TOUS");
  const [tripId, setTripId] = useState<string | null>(null);
  const t = tripId ? tripById(tripId) : undefined;
  const trips = tripsFor(range, q, cls);
  const pax = t ? list.filter((r) => r.trip === t.id && r.pay !== "Remboursé" && r.board !== "Annulé").sort((a, b) => (a.seat || 999) - (b.seat || 999)) : [];
  const rem = t ? remainingSeats(t, list) : 0;

  return (
    <Overlay onClose={onClose}>
      <div className="w-full max-w-[940px] max-h-[90vh] flex flex-col bg-white rounded-2xl overflow-hidden shadow-[0_30px_70px_rgba(31,27,46,0.35)] text-[#1f1b2e] text-[13px]">
        <div className="px-5 pt-[18px] pb-3 text-center">
          <div className="text-lg font-bold text-[#241f36]">{t ? `Bordereau · ${t.from} → ${t.to}` : "Liste des voyages"}</div>
          <div className="text-[12.5px] text-[#6b6480] mt-0.5">{t ? `${fDateCap(t.date)} à ${t.heure} · ${t.bus} · ${t.cls}` : "Choisissez un départ pour afficher son bordereau"}</div>
        </div>
        {!t ? (
          <>
            <div className="flex items-center gap-3.5 flex-wrap px-5 pb-3">
              <RangePicker value={range} onChange={setRange} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Recherche par ville / bus" className="flex-1 min-w-[160px] h-9 px-3 rounded-[9px] bg-[#e9e6f2]" />
              <select value={cls} onChange={(e) => setCls(e.target.value)} className="h-9 px-2.5 border border-[#e2e8f0] rounded-[9px] bg-white">
                {["TOUS", "CLASSIQUE", "VIP"].map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-2.5 grid gap-1.5 content-start">
              {trips.map((x) => (
                <TripRow key={x.id} t={x} list={list} btnLabel="Bordereau" compact onPick={() => setTripId(x.id)} />
              ))}
              {trips.length === 0 && <div className="p-6 text-center text-[#8b81a8]">Aucun voyage pour ces critères.</div>}
            </div>
          </>
        ) : (
          <>
            <div className="flex gap-2.5 flex-wrap px-5 pb-3">
              {[
                { label: "Billets émis", value: String(pax.filter((r) => r.ticket).length), color: "#5b21b6" },
                { label: "Non payés", value: String(pax.filter((r) => r.pay !== "Payé").length), color: "#b91c1c" },
                { label: "Embarqués", value: String(pax.filter((r) => r.board === "Embarqué").length), color: "#047857" },
                { label: "Places restantes", value: `${rem} / ${t.cap}`, color: "#241f36" },
              ].map((k) => (
                <div key={k.label} className="flex-[1_1_120px] px-3 py-2.5 bg-[#f8f7fc] rounded-[10px]">
                  <div className="text-[11px] text-[#8b81a8]">{k.label}</div>
                  <div className="text-[17px] font-bold mt-0.5" style={{ color: k.color }}>
                    {k.value}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-2.5">
              {pax.map((r) => {
                const p = PAY_STYLE[r.pay];
                const bd = BOARD_STYLE[r.board];
                const emb = r.board === "Embarqué";
                return (
                  <div key={r.id} className="flex items-center gap-x-3.5 gap-y-2.5 flex-wrap py-2 px-1 border-b border-[#f1f0f5]">
                    <span className="w-[34px] h-[30px] flex items-center justify-center rounded-[7px] bg-[#ede9fe] text-xs font-bold text-[#5b21b6]">{r.seat ?? "—"}</span>
                    <span className="flex-[1_1_180px] min-w-0">
                      <span className="block text-[13px] font-bold text-[#241f36]">{r.name}</span>
                      <span className="block font-mono text-[11px] text-[#6b6480]">{r.phone}</span>
                    </span>
                    <span className="font-mono text-[11.5px]" style={{ color: r.ticket ? "#5b21b6" : "#a1a1aa" }}>
                      {r.ticket || "non émis"}
                    </span>
                    <span className="px-2 py-0.5 rounded-md text-[11px] font-bold" style={{ backgroundColor: p.bg, color: p.fg }}>
                      {r.pay}
                    </span>
                    <span className="px-2 py-0.5 rounded-md text-[11px] font-bold" style={{ backgroundColor: bd.bg, color: bd.fg }}>
                      {r.board}
                    </span>
                    <button
                      type="button"
                      onClick={() => onToggleBoard(r.id, emb ? "En attente" : "Embarqué")}
                      className="h-[30px] px-3 border border-[#d4d4d8] rounded-lg text-xs font-bold"
                      style={{ background: emb ? "#fff" : "#dcfce7", color: emb ? "#64748b" : "#15803d" }}
                    >
                      {emb ? "Annuler" : "Embarquer"}
                    </button>
                  </div>
                );
              })}
              {pax.length === 0 && <div className="p-6 text-center text-[#8b81a8]">Aucun passager enregistré sur ce départ dans la console.</div>}
            </div>
          </>
        )}
        <div className="flex justify-center gap-2.5 flex-wrap px-5 pt-3 pb-4 border-t border-[#f1f0f5]">
          {t ? (
            <>
              <button type="button" onClick={() => setTripId(null)} className="h-10 px-4 border border-[#d4d4d8] bg-white rounded-[9px] font-semibold">
                ← Liste des voyages
              </button>
              <button type="button" onClick={() => onFlash(`Bordereau imprimé — ${t.bus}`)} className="h-10 px-4 bg-[#dcfce7] rounded-[9px] font-bold text-[#15803d]">
                Imprimer le bordereau
              </button>
            </>
          ) : (
            <button type="button" onClick={onOpenTripResa} className="h-10 px-4 border border-[#d4d4d8] bg-white rounded-[9px] font-semibold">
              Réservation sur voyage indéterminé
            </button>
          )}
          <button type="button" onClick={onClose} className="min-w-[200px] h-10 px-4 bg-[#ef4444] rounded-[9px] font-bold text-white">
            Fermer
          </button>
        </div>
      </div>
    </Overlay>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Thermal ticket                               */
/* -------------------------------------------------------------------------- */

export function TicketModal({
  reservation: r,
  company = "EASYCAR EXPRESS",
  onClose,
  onFlash,
}: {
  reservation: ReservationItem;
  company?: string;
  onClose: () => void;
  onFlash: (msg: string) => void;
}) {
  const t = tripById(r.trip);
  const seat = r.seat ? `A-${r.seat}` : "—";
  const xaf = (n: number) => `XAF ${money(n).replace(" FCFA", "")}`;
  type F = { label: string; value: string; blue?: boolean; big?: boolean };
  const fields: F[] = [
    { label: "Téléphone", value: r.phone, blue: true },
    { label: "Carte nationale", value: r.cni },
    { label: "Type de ticket", value: r.cls },
    { label: "Itinéraire", value: `${r.from}-${r.to}`, blue: true },
    { label: "Date de voyage", value: fDash(r.date), blue: true },
    { label: "Prix", value: xaf(r.montant), blue: true },
    { label: "Type de voyage", value: "ALLER" },
    { label: "Bus", value: t ? t.bus : "À l'embarquement", blue: true },
    { label: "Ticket", value: r.ticket, blue: true },
    { label: "Bagage", value: r.bag ? "OUI" : "NON" },
    { label: "N° siège", value: seat, big: true },
    { label: "Remboursement", value: r.pay === "Remboursé" ? xaf(r.montant) : "XAF -" },
  ];
  const stub: F[] = [
    { label: "Itinéraire", value: `${r.from}-${r.to}` },
    { label: "Carte nationale", value: r.cni },
    { label: "Passager", value: r.name },
    { label: "Téléphone", value: r.phone },
    { label: "Départ le", value: `${fDash(r.date)}${r.heure ? ` ${r.heure}` : ""}` },
    { label: "N° siège", value: seat },
    { label: "Prix", value: xaf(r.montant) },
    { label: "Dette", value: r.montant - r.paid > 0 && r.pay !== "Remboursé" ? xaf(r.montant - r.paid) : "XAF -" },
  ];
  const created = r.confirmedAt ? `${fDash(r.confirmedAt)} ${r.confirmedAt.slice(11, 16)}` : fDash(r.createdAt);
  const expire = r.date ? `${fDash(r.date)} ${r.heure || "00:00"}` : "—";

  return (
    <Overlay z="z-[90]" onClose={onClose}>
      <div className="w-full max-w-[250px] max-h-[92vh] overflow-y-auto px-4 py-[18px] bg-white rounded-xl shadow-[0_30px_70px_rgba(31,27,46,0.35)] text-[#1f1b2e]">
        <div className="flex gap-2 border-l-[10px] border-[#ede9fe] pl-2">
          <div className="flex-1 min-w-0">
            <div className="flex justify-between gap-2">
              <div>
                <div className="text-[11px] font-bold text-[#6d28d9]">{company}</div>
                <div className="text-[8.5px] italic text-[#3f3a52]">(+237) 681 583 469 · 694 866 796</div>
                <div className="text-[8.5px] italic text-[#3f3a52]">support@{company.toLowerCase().split(" ")[0]}.com</div>
              </div>
              <div className="text-right text-[8.5px] italic text-[#3f3a52]">{r.confirmedBy || r.createdBy}</div>
            </div>
            <div className="text-[15px] font-bold mt-3 leading-tight">
              {r.seat || "—"}. {r.name}
            </div>
            <div className="text-[15px] font-bold">({seat})</div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-2 mt-2.5">
              {fields.map((f) => (
                <div key={f.label}>
                  <div className="text-[7.5px] font-semibold uppercase" style={{ color: f.blue ? "#6d28d9" : "#3f3a52" }}>
                    {f.label}
                  </div>
                  <div className={classNames("mt-px", f.big ? "text-sm font-bold" : "text-[10.5px] font-medium")}>{f.value || "-"}</div>
                </div>
              ))}
            </div>
            <div className="text-[7.5px] text-[#3f3a52] mt-2">
              Créé le {created} · Expire le {expire}
            </div>
          </div>
        </div>
        <div className="mt-2 px-1.5 py-1 bg-[#6d28d9] text-center text-[8px] font-bold italic text-white">
          "Votre satisfaction est notre priorité"
          <br />
          {company} : (+237) 681 583 469 · 694 866 796
        </div>
        <div className="mt-1.5 border-t-[1.5px] border-dashed border-[#6d28d9]" />
        <div className="grid grid-cols-2 gap-2 mt-2 pb-2 border-b border-[#3f3a52]">
          {stub.map((f) => (
            <div key={f.label}>
              <div className="text-[7.5px] font-semibold uppercase text-[#6d28d9]">{f.label}</div>
              <div className="text-[10px] mt-px">{f.value || "-"}</div>
            </div>
          ))}
        </div>
        <div className="text-[7.5px] text-[#6b6480] mt-1.5">NB : présence en agence au plus tard 30 min avant l'heure du départ.</div>
        <div className="flex gap-2 mt-3.5">
          <button type="button" onClick={() => onFlash(`Ticket ${r.ticket} envoyé à l'imprimante`)} className="flex-1 h-[34px] bg-[#6d28d9] rounded-lg text-xs font-bold text-white">
            Imprimer le ticket
          </button>
          <button type="button" onClick={onClose} className="h-[34px] px-3 border border-[#d4d4d8] bg-white rounded-lg text-xs font-semibold">
            Fermer
          </button>
        </div>
        <button type="button" onClick={() => onFlash(`Ticket envoyé par WhatsApp à ${r.phone}`)} className="w-full h-8 mt-2 bg-[#dcfce7] rounded-lg text-xs font-bold text-[#15803d]">
          Envoyer par WhatsApp
        </button>
      </div>
    </Overlay>
  );
}

/* -------------------------------------------------------------------------- */
/*                         Payment link / reference                           */
/* -------------------------------------------------------------------------- */

export function PaymentLinkModal({ rows, onClose, onConfirm }: { rows: ReservationItem[]; onClose: () => void; onConfirm: () => void }) {
  return (
    <Overlay z="z-[88]" onClose={onClose}>
      <div className="w-full max-w-[440px] p-[22px] bg-white rounded-[14px] shadow-[0_24px_60px_rgba(31,27,46,0.28)]">
        <div className="text-[17px] font-bold text-[#241f36]">
          Générer un lien de paiement pour {rows.length} réservation{rows.length > 1 ? "s" : ""}
        </div>
        <div className="flex gap-1.5 flex-wrap mt-3">
          {rows.map((r) => (
            <span key={r.id} className="px-2 py-1 bg-[#dcfce7] rounded-md text-[11.5px] font-semibold text-[#166534] whitespace-nowrap">
              {r.name}
            </span>
          ))}
        </div>
        <div className="text-[12.5px] text-[#6b6480] mt-3 leading-normal">
          Un lien Orange Money / MTN MoMo est envoyé par SMS et WhatsApp. Le billet est émis automatiquement dès que le paiement est reçu.
        </div>
        <div className="flex gap-2.5 mt-[18px]">
          <button type="button" onClick={onConfirm} disabled={rows.length === 0} className="h-[38px] px-[18px] bg-[#eab308] rounded-[9px] text-[13px] font-bold text-white disabled:opacity-50">
            Confirmer
          </button>
          <button type="button" onClick={onClose} className="h-[38px] px-4 border border-[#e2e8f0] bg-white rounded-[9px] text-[13px] font-semibold text-[#475569]">
            Annuler
          </button>
        </div>
      </div>
    </Overlay>
  );
}

export function ModifRefModal({
  reservation: r,
  onClose,
  onSave,
}: {
  reservation: ReservationItem;
  onClose: () => void;
  onSave: (om: string, momo: string) => void;
}) {
  const [om, setOm] = useState(r.refOM);
  const [momo, setMomo] = useState(r.refMomo);
  const inputCls = "w-full h-[38px] px-3 border border-[#e2e8f0] rounded-[9px] font-mono text-[13px]";
  const labelCls = "block text-[11px] font-bold tracking-[0.06em] uppercase text-[#8b81a8] mb-1";
  return (
    <Overlay z="z-[88]" onClose={onClose}>
      <div className="w-full max-w-[420px] p-[22px] bg-white rounded-2xl shadow-[0_24px_60px_rgba(31,27,46,0.28)]">
        <div className="text-base font-bold text-[#241f36]">Modifier la référence de paiement</div>
        <div className="text-[12.5px] text-[#6b6480] mt-1">
          {r.pre || r.ticket || "Réservation"} · {r.name} · {money(r.montant)}
        </div>
        <label className="block mt-[18px]">
          <span className={labelCls}>Référence Orange Money</span>
          <input value={om} onChange={(e) => setOm(e.target.value)} placeholder="MP280926.1432.A12345" className={inputCls} />
        </label>
        <label className="block mt-3">
          <span className={labelCls}>Référence MTN MoMo</span>
          <input value={momo} onChange={(e) => setMomo(e.target.value)} placeholder="MM280926.1432.883201" className={inputCls} />
        </label>
        <div className="flex gap-2.5 mt-5">
          <button type="button" onClick={onClose} className="flex-1 h-[38px] border border-[#e2e8f0] bg-white rounded-[9px] text-[13px] font-semibold text-[#64748b]">
            Annuler
          </button>
          <button type="button" onClick={() => onSave(om.trim(), momo.trim())} className="flex-1 h-[38px] bg-[#6d28d9] rounded-[9px] text-[13px] font-bold text-white">
            Enregistrer
          </button>
        </div>
      </div>
    </Overlay>
  );
}

/* -------------------------------------------------------------------------- */
/*                       Reservation full-screen view                         */
/* -------------------------------------------------------------------------- */

export type DzAction = { label: string; run: () => void; kind?: "p" | "g" };

export function ReservationFullscreen({
  r,
  list,
  index,
  onPrev,
  onNext,
  onClose,
  onOpenOther,
  actions,
}: {
  r: ReservationItem;
  list: ReservationItem[];
  index: string;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  onOpenOther: (id: string) => void;
  actions: DzAction[];
}) {
  const t = tripById(r.trip);
  const P = PAY_STYLE[r.pay];
  const B = BOARD_STYLE[r.board];
  const C = CLS_STYLE[r.cls] || CLS_STYLE.CLASSIQUE;
  const due = r.montant - r.paid;
  const refunded = r.pay === "Remboursé";
  const others = list.filter((x) => x.phone === r.phone && x.id !== r.id);
  const card = "p-[18px] bg-white border border-[#ece7f8] rounded-[14px]";
  const cardTitle = "text-[11px] font-bold tracking-[0.07em] uppercase text-[#8b81a8]";

  return (
    <div className="fixed inset-0 z-[78] flex flex-col bg-[#faf7ff] text-[#1f1b2e] text-[13px]">
      <div className="flex items-center gap-3 flex-wrap px-[22px] py-3.5 bg-white border-b border-[#ece7f8]">
        <button type="button" onClick={onPrev} title="Réservation précédente" className="w-[34px] h-[34px] border border-[#ece7f8] bg-white rounded-[9px] text-sm text-[#4c1d95]">
          ←
        </button>
        <button type="button" onClick={onNext} title="Réservation suivante" className="w-[34px] h-[34px] border border-[#ece7f8] bg-white rounded-[9px] text-sm text-[#4c1d95]">
          →
        </button>
        <span className="text-xs text-[#8b81a8]">{index}</span>
        <span className="px-2 py-1 rounded-[7px] font-mono text-[12.5px] whitespace-nowrap" style={{ background: r.pre ? "#dcfce7" : "#f4f4f5", color: r.pre ? "#166534" : "#a1a1aa" }}>
          {r.pre || "—"}
        </span>
        <span className="px-2 py-1 rounded-[7px] font-mono text-[12.5px] whitespace-nowrap" style={{ background: r.ticket ? "#ede9fe" : "#f4f4f5", color: r.ticket ? "#5b21b6" : "#a1a1aa" }}>
          {r.ticket || "à émettre"}
        </span>
        <span className="text-[17px] font-bold text-[#241f36]">{r.name}</span>
        <span className="px-2.5 py-1 rounded-[7px] text-xs font-bold" style={{ backgroundColor: P.bg, color: P.fg }}>
          {r.pay}
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[7px] text-xs font-bold" style={{ backgroundColor: B.bg, color: B.fg }}>
          <span className="w-[7px] h-[7px] rounded-full" style={{ background: B.dot }} />
          {r.board}
        </span>
        <div className="ml-auto flex gap-2 flex-wrap">
          {actions.map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={a.run}
              className="h-9 px-3 border rounded-[9px] text-[12.5px] font-bold whitespace-nowrap"
              style={{
                background: a.kind === "p" ? "#6d28d9" : a.kind === "g" ? "#dcfce7" : "#fff",
                color: a.kind === "p" ? "#fff" : a.kind === "g" ? "#15803d" : "#4c1d95",
                borderColor: a.kind === "p" ? "#6d28d9" : a.kind === "g" ? "#bbf7d0" : "#ddd6fe",
              }}
            >
              {a.label}
            </button>
          ))}
          <button type="button" onClick={onClose} className="flex items-center gap-1.5 h-9 px-3 bg-[#1f1b2e] rounded-[9px] text-[12.5px] font-bold text-white whitespace-nowrap">
            ⛶ Quitter le plein écran
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-[22px] pt-5 pb-10">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-4 items-start">
          <div className="grid gap-4">
            <div className={card}>
              <div className={cardTitle}>Passager</div>
              <div className="text-xl font-bold text-[#241f36] mt-2">{r.name}</div>
              <div className="grid grid-cols-2 gap-3 mt-3.5">
                <Info label="Téléphone" value={r.phone} mono />
                <Info label="Pièce d'identité" value={`CNI ${r.cni || "-"}`} />
                <Info label="Source" value={`${r.source}${r.agence ? ` · ${r.agence}` : ""}`} />
                <Info label="Créé par" value={`${r.createdBy}, ${fDate(r.createdAt)}`} />
              </div>
              {r.note && <div className="mt-3 px-3 py-2 bg-[#fef3c7] rounded-[9px] text-[12.5px] text-[#92400e]">{r.note}</div>}
            </div>
            <div className={card}>
              <div className={cardTitle}>Paiement</div>
              <div className="flex items-baseline justify-between gap-3 mt-2.5">
                <span className="text-[26px] font-bold text-[#241f36] whitespace-nowrap">{money(r.montant)}</span>
                <span className="text-[12.5px] font-semibold" style={{ color: refunded ? "#64748b" : due > 0 ? "#b91c1c" : "#16a34a" }}>
                  {refunded ? "remboursé" : due > 0 ? `reste ${money(due)}` : "encaissé"}
                </span>
              </div>
              <div className="grid gap-1.5 mt-3 pt-3 border-t border-[#f4f1fb] text-[12.5px] text-[#4a4361]">
                <Line label="Canal" value={<strong className="text-[#241f36]">{r.canal}</strong>} />
                <Line label="Réf. Orange Money" value={<span className="font-mono">{r.refOM || "–"}</span>} />
                <Line label="Réf. MTN MoMo" value={<span className="font-mono">{r.refMomo || "–"}</span>} />
                <Line label="Confirmation" value={r.confirmed ? `par ${r.confirmedBy}` : refunded ? "dossier clos" : "paiement non validé"} />
              </div>
            </div>
          </div>

          <div className={card}>
            <div className={cardTitle}>Voyage</div>
            <div className="flex items-center gap-2 mt-2.5">
              <span className="px-2.5 py-1 bg-[#ede9fe] rounded-[7px] font-bold text-[#5b21b6]">{r.from || "—"}</span>
              <span className="text-[#8b81a8]">→</span>
              <span className="px-2.5 py-1 bg-[#ffedd5] rounded-[7px] font-bold text-[#c2410c]">{r.to || "—"}</span>
              <span className="ml-auto px-2 py-0.5 rounded-[5px] text-[11px] font-bold" style={{ backgroundColor: C.bg, color: C.fg }}>
                {r.cls}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3.5">
              <Info label="Départ" value={r.date ? fDate(r.date) : "Date non fixée"} strong />
              <Info label="Heure" value={r.heure || "heure à définir"} strong />
              <Info label="Bus" value={t ? t.bus : "Non affecté"} strong />
              <div>
                <div className="text-[11px] text-[#8b81a8]">Siège</div>
                <div className="font-bold text-[#6d28d9] mt-0.5">{r.seat ? `Siège ${r.seat}` : "Non attribué"}</div>
              </div>
            </div>
            {t ? (
              <div className="mt-4 px-2.5 py-3.5 bg-[#f8f7fc] rounded-xl">
                <div className="flex justify-between flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#8b81a8] px-1.5 pb-2.5">
                  <span>Avant du bus · chauffeur</span>
                  <span>
                    {remainingSeats(t, list)} places restantes sur {t.cap}
                  </span>
                </div>
                <SeatMap t={t} taken={takenSet(t, list)} highlight={r.seat} small />
              </div>
            ) : (
              <div className="mt-4 p-3.5 border border-dashed border-[#ddd6fe] rounded-xl text-[12.5px] text-[#6b6480]">Aucun bus affecté. Utilisez « Confirmer » pour choisir un départ et un siège.</div>
            )}
          </div>

          <div className="grid gap-4">
            <div className={card}>
              <div className={cardTitle}>Historique du dossier</div>
              <div className="mt-2.5">
                {r.audit
                  .slice()
                  .reverse()
                  .map((h, k) => (
                    <div key={k} className="grid grid-cols-[86px_14px_minmax(0,1fr)] gap-2 pt-2.5">
                      <span className="font-mono text-[11px] text-[#6b6480] text-right">{h[0]}</span>
                      <span className="w-[9px] h-[9px] mt-[3px] rounded-full" style={{ background: k === 0 ? "#6d28d9" : "#cbc4dc" }} />
                      <span className="text-[12.5px] text-[#241f36] leading-snug">{h[1]}</span>
                    </div>
                  ))}
              </div>
            </div>
            {others.length > 0 && (
              <div className={card}>
                <div className={cardTitle}>Autres réservations de ce client</div>
                <div className="grid gap-2 mt-2.5">
                  {others.map((o) => {
                    const p = PAY_STYLE[o.pay];
                    return (
                      <button key={o.id} type="button" onClick={() => onOpenOther(o.id)} className="flex items-center gap-2.5 px-3 py-2.5 border border-[#ece7f8] bg-white rounded-[10px] text-left hover:bg-[#f8f5ff]">
                        <span className="flex-1 min-w-0">
                          <span className="block text-[12.5px] font-semibold text-[#241f36]">
                            {o.from || "—"} → {o.to || "—"}
                          </span>
                          <span className="block text-[11px] text-[#8b81a8] mt-0.5">
                            {o.ticket || o.pre || "sans code"} · {o.date ? `${fShort(o.date)} ${o.heure}` : "date à définir"}
                          </span>
                        </span>
                        <span className="px-2 py-0.5 rounded-md text-[11px] font-bold" style={{ backgroundColor: p.bg, color: p.fg }}>
                          {o.pay}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, mono, strong }: { label: string; value: string; mono?: boolean; strong?: boolean }) {
  return (
    <div>
      <div className="text-[11px] text-[#8b81a8]">{label}</div>
      <div className={classNames("text-[#241f36] mt-0.5", mono && "font-mono", strong ? "font-semibold" : "text-[12.5px]")}>{value}</div>
    </div>
  );
}

function Line({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-baseline gap-2.5">
      <span className="min-w-0">{label}</span>
      <span className="whitespace-nowrap">{value}</span>
    </div>
  );
}

